import React, { createContext, useContext, useState, useEffect } from 'react';

export const MODULE_CATALOGUE = [
  {
    id: 'board',
    name: 'Kanban Board',
    category: 'Work Views',
    icon: 'view_kanban',
    description: 'Visual multi-column task progression with custom workflows and WIP limits.',
    defaultEnabled: true,
    required: false
  },
  {
    id: 'list',
    name: 'Dense List & Table',
    category: 'Work Views',
    icon: 'table_rows',
    description: 'High-density tabular view with bulk editing, field sorting, and quick filters.',
    defaultEnabled: true,
    required: false
  },
  {
    id: 'calendar',
    name: 'Calendar & Schedule',
    category: 'Work Views',
    icon: 'calendar_month',
    description: 'Date-oriented planning, deadline tracking, and drag-to-reschedule interface.',
    defaultEnabled: true,
    required: false
  },
  {
    id: 'timeline',
    name: 'Gantt Timeline',
    category: 'Planning',
    icon: 'timeline',
    description: 'Long-term roadmap visualization with task dependencies and milestone tracking.',
    defaultEnabled: false,
    required: false
  },
  {
    id: 'sprints',
    name: 'Sprints & Backlog',
    category: 'Agile Software',
    icon: 'speed',
    description: 'Agile iteration cycles, backlog grooming, sprint velocity, and burndown charts.',
    defaultEnabled: true,
    required: false
  },
  {
    id: 'docs',
    name: 'Docs & Wiki',
    category: 'Knowledge',
    icon: 'menu_book',
    description: 'Rich-text knowledge base, project specifications, and integrated meeting notes.',
    defaultEnabled: true,
    required: false
  },
  {
    id: 'meetings',
    name: 'Meetings & Decisions',
    category: 'Collaboration',
    icon: 'groups',
    description: 'Meeting agendas, live discussion notes, decision logs, and 1-click action item to task conversions.',
    defaultEnabled: true,
    required: false
  },
  {
    id: 'automations',
    name: 'Visual Automations',
    category: 'Productivity',
    icon: 'smart_toy',
    description: 'No-code event triggers, automatic status updates, and notification routing.',
    defaultEnabled: false,
    required: false
  },
  {
    id: 'game_dev',
    name: 'Game Development Studio',
    category: 'Specialized',
    icon: 'sports_esports',
    description: 'Game design docs, visual storyboards, level planning, asset pipeline, and playtest logs.',
    defaultEnabled: false,
    required: false
  },
  {
    id: 'hardware',
    name: 'Hardware Engineering & BOM',
    category: 'Specialized',
    icon: 'memory',
    description: 'Component bill of materials (BOM), schematics, PCB revisions, and validation tests.',
    defaultEnabled: false,
    required: false
  },
  {
    id: 'research',
    name: 'Research & Hypotheses',
    category: 'Specialized',
    icon: 'science',
    description: 'Hypothesis tracking, experiment runs, dataset registries, and literature reviews.',
    defaultEnabled: false,
    required: false
  },
  {
    id: 'public_portal',
    name: 'Public Portal & Feedback',
    category: 'Community',
    icon: 'public',
    description: 'Public roadmap sharing, community feedback board, voting, and changelogs.',
    defaultEnabled: false,
    required: false
  },
  {
    id: 'reports',
    name: 'Reporting & Project Health',
    category: 'Analytics',
    icon: 'monitoring',
    description: 'Project health score, delivery velocity, cycle time, bottleneck detection, and discipline workload.',
    defaultEnabled: true,
    required: false
  },
  {
    id: 'product',
    name: 'Product Strategy & Discovery',
    category: 'Product',
    icon: 'lightbulb',
    description: 'Product vision, opportunity solution trees, customer discovery, and RICE feature prioritization.',
    defaultEnabled: false,
    required: false
  },
  {
    id: 'creative',
    name: 'Design & Creative Studio',
    category: 'Creative',
    icon: 'palette',
    description: 'Visual moodboards, design briefs, multi-version concept iterations, brand assets, and reviews.',
    defaultEnabled: false,
    required: false
  }
];

export const METHODOLOGY_PACKS = [
  {
    id: 'software',
    name: 'Software Development',
    icon: 'terminal',
    category: 'Engineering',
    description: 'Agile workflow with sprint cycles, backlog management, bug tracking, releases, and git integrations.',
    recommendedModules: ['board', 'list', 'sprints', 'calendar', 'docs', 'timeline']
  },
  {
    id: 'game_dev',
    name: 'Game Development Studio',
    icon: 'sports_esports',
    category: 'Interactive Media',
    description: 'Full studio workflow covering game design, storyboards, levels, assets, builds & playtests.',
    recommendedModules: ['board', 'game_dev', 'list', 'docs', 'calendar', 'timeline']
  },
  {
    id: 'hardware',
    name: 'Hardware & Electronics',
    icon: 'memory',
    category: 'Engineering',
    description: 'Engineered for physical prototyping, components, BOM cost tracking, and test plans.',
    recommendedModules: ['board', 'hardware', 'list', 'docs', 'timeline']
  },
  {
    id: 'research',
    name: 'Scientific Research',
    icon: 'science',
    category: 'Academia & Science',
    description: 'Structured scientific discovery with hypotheses, experiment logging, and dataset tracking.',
    recommendedModules: ['list', 'research', 'docs', 'calendar']
  },
  {
    id: 'design',
    name: 'Design & Creative Studio',
    icon: 'palette',
    category: 'Creative',
    description: 'Visual moodboarding, concept iterations, asset reviews, and client deliverables.',
    recommendedModules: ['board', 'docs', 'calendar', 'public_portal']
  },
  {
    id: 'marketing',
    name: 'Marketing & Growth',
    icon: 'campaign',
    category: 'Growth',
    description: 'Campaign tracking, editorial calendar, launch roadmaps, public portals, and feedback.',
    recommendedModules: ['board', 'calendar', 'docs', 'public_portal', 'automations']
  },
  {
    id: 'education',
    name: 'Education & Curriculum',
    icon: 'school',
    category: 'Academia',
    description: 'Course planning, lesson scheduling, student milestones, syllabus docs, and deadlines.',
    recommendedModules: ['calendar', 'docs', 'board', 'list']
  },
  {
    id: 'freelance',
    name: 'Freelance & Client Services',
    icon: 'handshake',
    category: 'Consulting',
    description: 'Client deliverables, project milestones, task lists, schedule, and transparent public feedback.',
    recommendedModules: ['board', 'list', 'calendar', 'docs', 'public_portal']
  },
  {
    id: 'personal',
    name: 'Minimal Personal Work',
    icon: 'person',
    category: 'Personal',
    description: 'Ultra clean and lightweight: boards, dense lists, and a calendar for personal projects.',
    recommendedModules: ['board', 'list', 'calendar']
  },
  {
    id: 'custom',
    name: 'Custom Modular Configuration',
    icon: 'tune',
    category: 'Custom',
    description: 'Start from a clean slate and hand-pick the exact module suite tailored to your workflow.',
    recommendedModules: ['board', 'list']
  }
];

const ModuleContext = createContext();

export const ModuleProvider = ({ children }) => {
  const [enabledModules, setEnabledModules] = useState(() => {
    const saved = localStorage.getItem('forma_enabled_modules');
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch (e) {
        return ['board', 'list', 'calendar', 'sprints', 'docs'];
      }
    }
    return ['board', 'list', 'calendar', 'sprints', 'docs'];
  });

  const [activeMethodology, setActiveMethodology] = useState('software');

  useEffect(() => {
    localStorage.setItem('forma_enabled_modules', JSON.stringify(enabledModules));
  }, [enabledModules]);

  const toggleModule = (moduleId) => {
    setEnabledModules((prev) => {
      if (prev.includes(moduleId)) {
        return prev.filter((id) => id !== moduleId);
      } else {
        return [...prev, moduleId];
      }
    });
  };

  const enableModule = (moduleId) => {
    if (!enabledModules.includes(moduleId)) {
      setEnabledModules((prev) => [...prev, moduleId]);
    }
  };

  const disableModule = (moduleId) => {
    setEnabledModules((prev) => prev.filter((id) => id !== moduleId));
  };

  const applyMethodology = (packId, customModuleList = null) => {
    const pack = METHODOLOGY_PACKS.find((p) => p.id === packId);
    if (pack) {
      setActiveMethodology(packId);
      const targetModules = customModuleList || pack.recommendedModules;
      const merged = Array.from(new Set([...enabledModules, ...targetModules]));
      setEnabledModules(merged);
    }
  };

  const isModuleEnabled = (moduleId) => enabledModules.includes(moduleId);

  return (
    <ModuleContext.Provider
      value={{
        modules: MODULE_CATALOGUE,
        methodologies: METHODOLOGY_PACKS,
        enabledModules,
        setEnabledModules,
        activeMethodology,
        toggleModule,
        enableModule,
        disableModule,
        applyMethodology,
        isModuleEnabled
      }}
    >
      {children}
    </ModuleContext.Provider>
  );
};

export const useModules = () => useContext(ModuleContext);
